import React from "react";

// Presentational numeric inputs shared by the Age / Height / Weight text-input
// modals. Inputs are digits-only by default (inputMode="numeric"
// pattern="[0-9]*") so iOS shows the native numeric keypad; the height fields
// opt into one decimal place via `allowDecimal`, which switches the keypad to
// inputMode="decimal" so it carries a separator key. The visible value
// auto-sizes with `ch` so the number (+ optional unit suffix) stays centred.

const digitsOnly = (raw: string) => raw.replace(/[^0-9]/g, "");

/**
 * Digits plus at most one decimal point followed by at most one digit.
 *
 * Deliberately keeps a trailing `.` ("170.") rather than stripping it: these
 * inputs are controlled, so dropping the separator would echo the previous
 * value back and the key press would look like it did nothing. `parseFloat`
 * ignores it, and `validate()` guards the degenerate "." on its own.
 *
 * The comma replace is for iOS: on a locale whose decimal separator is `,` the
 * decimal keypad offers that key and nothing else, so stripping it would make
 * the field un-typeable on most of Europe.
 */
const oneDecimal = (raw: string) => {
  const cleaned = raw.replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  return rest.length === 0 ? whole : `${whole}.${rest.join("").slice(0, 1)}`;
};

const sanitize = (raw: string, allowDecimal?: boolean) =>
  allowDecimal ? oneDecimal(raw) : digitsOnly(raw);

/** Attributes that pick the iOS keypad — decimal one carries a separator key. */
const keypad = (allowDecimal?: boolean) =>
  allowDecimal
    ? ({ inputMode: "decimal", pattern: "[0-9]*[.,]?[0-9]*" } as const)
    : ({ inputMode: "numeric", pattern: "[0-9]*" } as const);

const widthFor = (value: string) => `${Math.max(value.length, 1)}ch`;

/**
 * Make the whole bordered box a tap target for its input.
 *
 * The inputs auto-size to their digits — 1ch, roughly 12px, while empty — so
 * the real target is a sliver in the middle of a 52px-tall full-width box and
 * the user has to aim at it. Widening the input instead would break the layout:
 * the `ch` sizing is what keeps `178 cm` and `5' 10"` centred as a unit.
 *
 * Routes to the input nearest the tap, so the two-field height row sends the
 * left half to feet and the right half to inches. Focusing synchronously inside
 * the click keeps the call inside the user gesture, which is what iOS requires
 * before it will raise the keypad.
 */
const focusNearestInput = (event: React.MouseEvent<HTMLDivElement>) => {
  // A direct tap on an input already places its own caret — don't fight it.
  if ((event.target as HTMLElement).tagName === "INPUT") return;

  const inputs = Array.from(
    event.currentTarget.querySelectorAll<HTMLInputElement>(
      "input.text-input-el",
    ),
  );
  if (inputs.length === 0) return;

  const distanceToTap = (input: HTMLInputElement) => {
    const rect = input.getBoundingClientRect();
    return Math.abs(rect.left + rect.width / 2 - event.clientX);
  };
  const nearest = inputs.reduce((closest, input) =>
    distanceToTap(input) < distanceToTap(closest) ? input : closest,
  );

  nearest.focus();
  nearest.setSelectionRange(nearest.value.length, nearest.value.length);
};

/**
 * Normalise a stored picker value into the digits-only form these fields hold.
 *
 * Values written by the old wheel carry a unit suffix and may carry a half step
 * (`5'`, `10.5"`, `0.5"`), and such drafts survive in localStorage across the
 * upgrade. Stripping non-digits would fold `10.5"` into `105`; `parseInt` stops
 * at the first non-digit instead, truncating to the whole number these fields
 * model. Returns "" when there is no leading number.
 */
export const seedDigits = (raw?: string): string => {
  const n = parseInt(raw ?? "", 10);
  return isNaN(n) ? "" : String(n);
};

/**
 * The `seedDigits` counterpart for the fields that accept one decimal place.
 *
 * `parseInt` truncates, so seeding a height draft with it would silently drop
 * the fraction every time the modal reopened — `10.5"` back as `10"`. Rounds to
 * the one decimal place the fields can render, which also folds a legacy wheel
 * value (`10.5"`) into exactly the same shape a typed one has.
 */
export const seedDecimal = (raw?: string): string => {
  const n = parseFloat(raw ?? "");
  return isNaN(n) ? "" : String(Math.round(n * 10) / 10);
};

interface NumberFieldProps {
  value: string; // digits only ("" when empty), or one decimal when allowDecimal
  onChange: (digits: string) => void;
  hasError: boolean;
  ariaLabel: string;
  suffix?: string; // "cm" | "lb" | "kg" — hidden while empty
  maxLength?: number;
  /** Accept one decimal place (`170.5`). Off by default — Age and Weight are whole. */
  allowDecimal?: boolean;
  onSubmit?: () => void;
}

/** Single numeric field with an optional unit suffix (Age, Weight, Height·cm). */
export const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  hasError,
  ariaLabel,
  suffix,
  maxLength = 3,
  allowDecimal,
  onSubmit,
}) => (
  <div
    className={`text-input-field ${hasError ? "error" : ""}`}
    onClick={focusNearestInput}
  >
    <span className="text-input-measure">
      <input
        className="text-input-el"
        {...keypad(allowDecimal)}
        maxLength={maxLength}
        aria-label={ariaLabel}
        value={value}
        style={{ width: widthFor(value) }}
        onChange={(e) => onChange(sanitize(e.target.value, allowDecimal))}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit?.();
        }}
      />
      {value && suffix ? (
        <span className="text-input-suffix">{suffix}</span>
      ) : null}
    </span>
  </div>
);

interface FeetInchesFieldProps {
  feet: string; // digits only
  inches: string; // digits only
  onFeetChange: (digits: string) => void;
  onInchesChange: (digits: string) => void;
  hasError: boolean;
  onSubmit?: () => void;
}

/**
 * Two fields rendered as `F' II"` (Height·in). Height-only, so the inches half
 * takes one decimal place unconditionally (`10.5"`); feet stays whole, because
 * a fraction there is ambiguous next to an inches box that already expresses it.
 */
export const FeetInchesField: React.FC<FeetInchesFieldProps> = ({
  feet,
  inches,
  onFeetChange,
  onInchesChange,
  hasError,
  onSubmit,
}) => (
  <div
    className={`text-input-field ${hasError ? "error" : ""}`}
    onClick={focusNearestInput}
  >
    <span className="text-input-measure">
      <span className="text-input-seg">
        <input
          className="text-input-el"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          aria-label="Feet"
          value={feet}
          style={{ width: widthFor(feet) }}
          onChange={(e) => onFeetChange(digitsOnly(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit?.();
          }}
        />
        <span className="text-input-suffix">&#39;</span>
      </span>
      <span className="text-input-seg">
        <input
          className="text-input-el"
          {...keypad(true)}
          maxLength={4} // "11.5"
          aria-label="Inches"
          value={inches}
          style={{ width: widthFor(inches) }}
          onChange={(e) => onInchesChange(oneDecimal(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit?.();
          }}
        />
        <span className="text-input-suffix">&#34;</span>
      </span>
    </span>
  </div>
);
